import { reloadForOrganizationChange, setLastOrganizationId } from './organization-selection'

const ORGANIZATION_SYNC_CHANNEL = 'doss_organization_sync'

type OrganizationChangedMessage = {
  type: 'organization_changed'
  organizationId: string
}

/**
 * How a tab reacts to the session moving to a different organization. `keepUrl` is true when the tab
 * hasn't shown anything from the previous organization yet, so it only needs to read the cookie again.
 */
export type OrganizationChange = { keepUrl: boolean }

const isOrganizationChangedMessage = (data: unknown): data is OrganizationChangedMessage =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  'organizationId' in data &&
  data.type === 'organization_changed' &&
  typeof data.organizationId === 'string'

/**
 * Manages cross-tab organization synchronization. Encapsulates the state and BroadcastChannel
 * lifecycle for keeping multiple tabs in sync when the session switches organizations.
 */
export class OrganizationSyncManager {
  /** The organization this tab's session is signed in to, or null until this tab learns it */
  private tabOrganizationId: string | null = null

  /** An organization another tab switched to while this tab didn't know its own yet (still loading) */
  private organizationAnnouncedWhileLoading: string | null = null

  /** What this tab does when another tab moves the session to a different organization */
  private onOrganizationChange: ((change: OrganizationChange) => void) | null = null

  /** The BroadcastChannel for receiving messages from other tabs */
  private channel: BroadcastChannel | null = null

  /**
   * Records which organization this tab's session is in, without telling other tabs or changing the
   * last-used organization. For restoring a session on page load: a check-session response reports
   * the cookie as it was when the request was sent, so if another tab switched meanwhile, announcing
   * it would send the other tabs (including the one that just switched) back to the old organization.
   */
  rememberSessionOrganization(organizationId: string): void {
    const announced = this.organizationAnnouncedWhileLoading
    this.organizationAnnouncedWhileLoading = null
    this.tabOrganizationId = organizationId
    // Another tab switched while this check-session was in flight, so it may have read the cookie from
    // before the switch. Reload to read it again; the reload starts with nothing announced, so at most once.
    if (announced !== null && announced !== organizationId) {
      this.onOrganizationChange?.({ keepUrl: true })
    }
  }

  /**
   * Records that the session just moved to this organization (a sign-in, the picker, the switcher),
   * remembers it for the next sign-in, and tells this browser's other tabs.
   *
   * Every tab shares the one session cookie, so after a sign-in or switch the other tabs are signed
   * in to this organization too, while still showing (and polling) pages from the previous one,
   * which the API refuses as belonging to another organization.
   */
  setSessionOrganization(organizationId: string): void {
    // This tab chose the organization itself, so an announcement it heard earlier is out of date.
    // Set directly, not through rememberSessionOrganization, which would act on that announcement.
    this.organizationAnnouncedWhileLoading = null
    // This tab's own listener hears the message too, and ignores it because this now matches
    this.tabOrganizationId = organizationId
    setLastOrganizationId(organizationId)
    this.broadcastOrganizationChange(organizationId)
  }

  /**
   * Broadcasts an organization change to all other tabs via BroadcastChannel.
   * Creates a channel, sends the message, and immediately closes it.
   */
  private broadcastOrganizationChange(organizationId: string): void {
    if (typeof BroadcastChannel === 'undefined') {
      return
    }
    const channel = new BroadcastChannel(ORGANIZATION_SYNC_CHANNEL)
    const message: OrganizationChangedMessage = {
      type: 'organization_changed',
      organizationId,
    }
    channel.postMessage(message)
    channel.close()
  }

  /**
   * Listens for another tab signing the session in to a different organization, and then calls
   * `onChange` (by default `reloadForOrganizationChange`). A tab that doesn't know its organization
   * yet keeps the announcement until its check-session answers (see `rememberSessionOrganization`).
   * Returns a function that stops listening.
   */
  listenForOrganizationChanges(
    onChange: (change: OrganizationChange) => void = (change) =>
      reloadForOrganizationChange(window.location, change),
  ): () => void {
    if (typeof BroadcastChannel === 'undefined') {
      return () => undefined
    }
    this.onOrganizationChange = onChange
    this.channel = new BroadcastChannel(ORGANIZATION_SYNC_CHANNEL)
    this.channel.onmessage = (event: MessageEvent<unknown>) => {
      this.handleOrganizationChangeMessage(event, onChange)
    }
    return () => {
      this.stopListening(onChange)
    }
  }

  /**
   * Handles an incoming organization change message from another tab.
   */
  private handleOrganizationChangeMessage(
    event: MessageEvent<unknown>,
    onChange: (change: OrganizationChange) => void,
  ): void {
    if (!isOrganizationChangedMessage(event.data)) {
      return
    }
    const { organizationId } = event.data

    // Tab doesn't know its org yet - remember the announcement for later
    if (this.tabOrganizationId === null) {
      this.organizationAnnouncedWhileLoading = organizationId
      return
    }

    // Same org as current - ignore
    if (this.tabOrganizationId === organizationId) {
      return
    }

    // Different org - update and trigger onChange
    this.tabOrganizationId = organizationId
    onChange({ keepUrl: false })
  }

  /**
   * Stops listening for organization changes and cleans up the BroadcastChannel.
   */
  private stopListening(onChange: (change: OrganizationChange) => void): void {
    this.channel?.close()
    if (this.onOrganizationChange === onChange) {
      this.onOrganizationChange = null
    }
  }
}

// Singleton instance for managing organization sync across the app
export const syncManager = new OrganizationSyncManager()
